import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { switchMap } from 'rxjs';
import {
  HLM_DIALOG_SHELL, HlmBtnDirective, HlmCheckboxComponent, HlmIconComponent, HlmSpinnerComponent,
} from '../../../ui';
import { UserAccountApiService } from '../../../core/services/user-account-api.service';
import { LoggingService } from '../../../core/services/logging.service';
import { extractErrorMessage } from '../../../shared/utils/extract-error-message';

/**
 * Context for the confirm-mobile dialog: the account whose number is confirmed.
 *
 * @ticket EB-4453
 */
export interface ConfirmMobileDialogData {
  userAccountId: number;
  fullName: string;
  mobile: string;
}

/** Steps of the confirmation flow, see {@link ConfirmMobileDialogComponent}. */
type Phase = 'prompt' | 'sending' | 'code' | 'confirming';

/**
 * Confirms a user account's mobile number via SMS code (old-CMT flow, unchanged).
 *
 *   prompt     — "send a confirmation code via SMS?"
 *   sending    — POST sendverificationcode, then GET the code
 *   code       — AMIE SHOWS the code; the caller reads it out over the phone, the agent
 *                compares, ticks "is it correct?" and confirms (the agent never types it).
 *                A new code can be requested from here, as in old CMT.
 *   confirming — POST uids/verify; closes with `true` once the backend accepts the code
 *
 * While a request is in flight the dialog cannot be closed (Cancel, X, Escape, backdrop):
 * closing would cancel the request although the backend may already have processed it.
 * The code itself is never logged.
 *
 * @ticket EB-4453
 */
@Component({
  selector: 'cmt-confirm-mobile-dialog',
  standalone: true,
  imports: [...HLM_DIALOG_SHELL, HlmBtnDirective, HlmCheckboxComponent, HlmIconComponent, HlmSpinnerComponent],
  templateUrl: './confirm-mobile-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmMobileDialogComponent {
  readonly dialogRef = inject(MatDialogRef<ConfirmMobileDialogComponent, boolean>);
  readonly data: ConfirmMobileDialogData = inject(MAT_DIALOG_DATA);
  private readonly userAccountApiService = inject(UserAccountApiService);
  private readonly loggingService = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly phase = signal<Phase>('prompt');
  readonly code = signal<string | null>(null);
  readonly codeMatches = signal(false);
  readonly errorMsg = signal<string | null>(null);

  /** A request is in flight: no second request may start and the dialog may not be closed. */
  readonly busy = computed(() => this.phase() === 'sending' || this.phase() === 'confirming');

  /** Sends the SMS and fetches the code. Allowed from the prompt and, as a resend, from the code step. */
  sendCode(): void {
    if (this.busy()) return;

    this.errorMsg.set(null);
    this.codeMatches.set(false);
    this.setPhase('sending');

    this.userAccountApiService.sendVerificationCode(this.data.userAccountId).pipe(
      switchMap(() => this.userAccountApiService.getConfirmationToken(this.data.userAccountId)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => {
        if (!res?.uid) {
          this.fail('prompt', 'The confirmation code could not be retrieved');
          return;
        }
        this.loggingService.usage('mobile_confirmation.code_sent', { userAccountId: this.data.userAccountId });
        this.code.set(res.uid);
        this.setPhase('code');
      },
      error: (err) => this.fail('prompt', extractErrorMessage(err, 'Failed to send the confirmation code')),
    });
  }

  /** Confirms the shown code. Only valid once the agent ticked "is it correct?". */
  confirm(): void {
    const code = this.code();
    if (this.phase() !== 'code' || !code || !this.codeMatches()) return;

    this.errorMsg.set(null);
    this.setPhase('confirming');

    this.userAccountApiService.verifyCode(this.data.userAccountId, code).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (res) => {
        if (!res?.valid) {
          this.fail('code', 'The code was not accepted. Send a new code.');
          return;
        }
        this.loggingService.usage('mobile_confirmation.confirmed', { userAccountId: this.data.userAccountId });
        this.dialogRef.close(true);
      },
      error: (err) => this.fail('code', extractErrorMessage(err, 'Failed to confirm the code')),
    });
  }

  /** Closes without confirming. Ignored while a request is in flight. */
  cancel(): void {
    if (this.busy()) return;
    this.dialogRef.close(false);
  }

  private setPhase(phase: Phase): void {
    this.phase.set(phase);
    this.dialogRef.disableClose = this.busy();
  }

  /** Returns to `backTo` with an error; a rejected code also drops the tick so it must be re-confirmed. */
  private fail(backTo: Phase, message: string): void {
    this.errorMsg.set(message);
    if (backTo === 'code') this.codeMatches.set(false);
    this.setPhase(backTo);
  }
}
